import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../../config/firebase';
import { ref, push, set, get } from 'firebase/database';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { formatCurrency } from '../../utils/formatCurrency';
import { WorkspaceShell } from '../ui/Workspace';
import { useBranches } from '../../hooks/useBranches';
import { renameCourseBatchAssignments } from '../../utils/lmsMutations';

const initialFormData = {
  name: '',
  courseCode: '',
  type: 'physical',
  duration: {
    months: 1,
    isOneDay: false,
  },
  academicLevel: 'certificate',
  fees: {
    fullCourseFee: 0,
    registrationFee: 0,
    numberOfInstallments: 1,
    oneoffDiscount: 0,
  },
  batches: [],
  instructors: [],
  minimumQualifications: {
    olRequired: false,
    alRequired: false,
    workExperience: 0,
  },
  introVideoUrl: '',
  imageUrl: '',
  curriculum: [
    {
      title: '',
      description: '',
      order: 0,
    },
  ],
};

const defaultBatchSchedule = {
  days: ['MON'],
  timeSlot: '09:00-12:00',
  lessonsPerWeek: 1,
  notes: '',
};

const serializeCourseForm = (value) => JSON.stringify(value || {});

const CourseForm = ({ courseId, initialData }) => {
  const navigate = useNavigate();
  const { activeList: activeBranches, byId: branchesById } = useBranches();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [courseImage, setCourseImage] = useState(null);
  const [courseImagePreview, setCourseImagePreview] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Ensure initialData has all required nested objects
  const completeInitialData = useMemo(
    () =>
      initialData
        ? {
            ...initialFormData,
            ...initialData,
            minimumQualifications: {
              ...initialFormData.minimumQualifications,
              ...(initialData.minimumQualifications || {}),
            },
            fees: {
              ...initialFormData.fees,
              ...(initialData.fees || {}),
            },
            duration: {
              ...initialFormData.duration,
              ...(initialData.duration || {}),
            },
            batches: Array.isArray(initialData.batches)
              ? initialData.batches.map((batch) => ({
                  ...batch,
                  schedule: {
                    ...defaultBatchSchedule,
                    ...(batch.schedule || {}),
                  },
                }))
              : [],
          }
        : initialFormData,
    [initialData],
  );

  const [formData, setFormData] = useState(completeInitialData);
  const [savedFormSnapshot, setSavedFormSnapshot] = useState(() =>
    serializeCourseForm(completeInitialData),
  );
  const [availableInstructors, setAvailableInstructors] = useState([]);

  const isEditMode = !!courseId;
  const hasCourseChanges =
    !isEditMode || !!courseImage || serializeCourseForm(formData) !== savedFormSnapshot;
  const displayedImageUrl =
    courseImagePreview ||
    formData.imageUrl ||
    formData.coverImage ||
    initialData?.imageUrl ||
    initialData?.coverImage ||
    '';

  // Add the calculation functions
  const calculateInstallmentAmount = () => {
    const { fullCourseFee, registrationFee, numberOfInstallments } =
      formData.fees;
    return (fullCourseFee - registrationFee) / numberOfInstallments;
  };

  const calculateOneoffPayment = () => {
    const { fullCourseFee, oneoffDiscount } = formData.fees;
    return fullCourseFee - fullCourseFee * (oneoffDiscount / 100);
  };

  // Add handlers for form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      minimumQualifications: {
        ...prev.minimumQualifications,
        [name]: checked,
      },
    }));
  };

  const handleImageChange = (e) => {
    setCourseImage(e.target.files?.[0] || null);
  };

  useEffect(() => {
    if (!courseImage) {
      setCourseImagePreview('');
      return undefined;
    }

    const previewUrl = URL.createObjectURL(courseImage);
    setCourseImagePreview(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [courseImage]);

  useEffect(() => {
    setFormData(completeInitialData);
    setSavedFormSnapshot(serializeCourseForm(completeInitialData));
    setCourseImage(null);
  }, [courseId, completeInitialData]);

  // Add this function at the top of your component
  const generateBatchId = (courseCode, batch, existingBatches = []) => {
    const year = batch.startDate.year.toString().slice(-2); // Get last 2 digits of year
    const month = batch.startDate.month.toString().padStart(2, '0'); // Pad month with leading zero

    // Get existing batch numbers for this year/month to determine next sequence
    const existingSequences = existingBatches
      .filter(
        (b) =>
          b.startDate.year.toString().slice(-2) === year &&
          b.startDate.month.toString().padStart(2, '0') === month,
      )
      .map((b) => parseInt(b.batchId?.slice(-2) || '00'));

    const nextSequence = (Math.max(0, ...existingSequences) + 1)
      .toString()
      .padStart(2, '0');

    return `${courseCode}-${year}${month}-${nextSequence}`;
  };

  // Modify the addBatch function
  const addBatch = () => {
    setFormData((prev) => ({
      ...prev,
      batches: [
        ...prev.batches,
        {
          batchId: generateBatchId(
            formData.courseCode,
            {
              startDate: {
                year: new Date().getFullYear(),
                month: new Date().getMonth() + 1,
                date: new Date().getDate(),
              },
            },
            prev.batches,
          ),
          startDate: {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            date: new Date().getDate(),
          },
          maxStudents: 20,
          branchId: activeBranches[0]?.id || '',
          branchName: activeBranches[0]?.name || '',
          branch: activeBranches[0]?.slug || 'colombo',
          schedule: {
            ...defaultBatchSchedule,
          },
        },
      ],
    }));
  };

  const removeBatch = (index) => {
    setFormData((prev) => ({
      ...prev,
      batches: prev.batches.filter((_, i) => i !== index),
    }));
  };

  const handleBatchChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      batches: prev.batches.map((batch, i) => {
        if (i === index) {
          if (field.includes('.')) {
            const [parent, child] = field.split('.');
            return {
              ...batch,
              [parent]: {
                ...batch[parent],
                [child]: value,
              },
            };
          }
          return {
            ...batch,
            [field]: value,
          };
        }
        return batch;
      }),
    }));
  };

  const toggleBatchDay = (index, day) => {
    const currentDays = formData.batches[index]?.schedule?.days || [];
    const nextDays = currentDays.includes(day)
      ? currentDays.filter((item) => item !== day)
      : [...currentDays, day];

    handleBatchChange(index, 'schedule.days', nextDays);
  };

  const scheduleDayOptions = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  // Add this handler for instructor selection
  const handleInstructorChange = (instructorId) => {
    setFormData((prev) => ({
      ...prev,
      instructors: prev.instructors.includes(instructorId)
        ? prev.instructors.filter((id) => id !== instructorId)
        : [...prev.instructors, instructorId],
    }));
  };

  // Fetch instructors
  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const instructorsRef = ref(db, 'users');
        const snapshot = await get(instructorsRef);
        const users = snapshot.val() || {};

        const instructorsList = Object.entries(users)
          .filter(([_, user]) =>
            ['instructor', 'demonstrator'].includes(
              String(user.role || '').toLowerCase(),
            ),
          )
          .map(([id, user]) => ({
            id,
            name: `${user.firstName} ${user.lastName}`,
            email: user.email,
            specialization: user.specialization || '',
            status: user.status || 'active',
          }))
          .filter((instructor) => instructor.status === 'active');

        setAvailableInstructors(instructorsList);
      } catch (error) {
        console.error('Error fetching instructors:', error);
        setError('Failed to load instructors');
      }
    };

    fetchInstructors();
  }, []);

  // Curriculum handlers
  const handleCurriculumChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      curriculum: prev.curriculum.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const addCurriculumItem = () => {
    setFormData((prev) => ({
      ...prev,
      curriculum: [
        ...prev.curriculum,
        {
          title: '',
          description: '',
          order: prev.curriculum.length,
        },
      ],
    }));
  };

  const removeCurriculumItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      curriculum: prev.curriculum
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, order: i })),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      let imageUrl =
        formData.imageUrl ||
        formData.coverImage ||
        initialData?.imageUrl ||
        initialData?.coverImage ||
        '';
      if (courseImage) {
        const imageRef = storageRef(storage, `courses/${courseImage.name}`);
        const snapshot = await uploadBytes(imageRef, courseImage);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      // Create a clean version of the course data without unwanted fields
      const { batch, branch, ...cleanFormData } = formData; // Remove batch and branch from root level

      const courseData = {
        ...cleanFormData,
        imageUrl,
        installmentAmount: calculateInstallmentAmount(),
        oneoffPayment: calculateOneoffPayment(),
        createdAt: isEditMode ? formData.createdAt : new Date().toISOString(),
        status: 'active',
        curriculum: isEditMode ? formData.curriculum : [],
      };

      if (isEditMode) {
        const courseRef = ref(db, `courses/${courseId}`);
        await set(courseRef, courseData);
        const previousBatches = Array.isArray(initialData?.batches)
          ? initialData.batches
          : [];
        const renamedBatches = (courseData.batches || [])
          .map((batchItem, index) => ({
            previous: previousBatches[index],
            current: batchItem,
            index,
          }))
          .filter(
            ({ previous, current }) =>
              previous?.batchId &&
              current?.batchId &&
              previous.batchId !== current.batchId,
          );
        await Promise.all(
          renamedBatches.map(({ previous, current, index }) =>
            renameCourseBatchAssignments({
              courseId,
              batchIndex: index,
              oldBatchId: previous.batchId,
              newBatchId: current.batchId,
              branchId: current.branchId,
              branchName: current.branchName,
            }),
          ),
        );
        setFormData(courseData);
        setSavedFormSnapshot(serializeCourseForm(courseData));
        setCourseImage(null);
        setSuccessMessage('Course updated successfully!');
      } else {
        const coursesRef = ref(db, 'courses');
        const newCourseRef = push(coursesRef);
        await set(newCourseRef, courseData);
        navigate('/courses/available');
      }
    } catch (err) {
      setError(
        `Failed to ${
          isEditMode ? 'update' : 'create'
        } course. Please try again.`,
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <WorkspaceShell
      title={isEditMode ? 'Edit Course' : 'Add New Course'}
      description="Set up catalog details, fees, batches, instructors, and curriculum."
    >
      <div className="mx-auto w-full max-w-4xl space-y-6">
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Course Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Course Code
            </label>
            <input
              type="text"
              name="courseCode"
              value={formData.courseCode}
              onChange={handleChange}
              placeholder="e.g., WD for Web Development"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              required
            />
            <p className="mt-1 text-sm text-gray-500">
              Short code used for batch identification
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Course Type
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
            >
              <option value="physical">Physical</option>
              <option value="online">Online</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Academic Level
            </label>
            <select
              name="academicLevel"
              value={formData.academicLevel}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
            >
              <option value="certificate">Certificate</option>
              <option value="diploma">Diploma</option>
              <option value="nvq3">NVQ Level 3</option>
              <option value="nvq4">NVQ Level 4</option>
            </select>
          </div>
        </div>

        {/* Duration Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Course Duration (months)
            </label>
            <input
              type="number"
              name="duration.months"
              value={formData.duration.months}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              min="1"
              required
              disabled={formData.duration.isOneDay}
            />
          </div>

          <div className="flex items-center mt-6">
            <input
              type="checkbox"
              id="isOneDay"
              name="duration.isOneDay"
              checked={formData.duration.isOneDay}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  duration: {
                    months: e.target.checked ? 0 : prev.duration.months,
                    isOneDay: e.target.checked,
                  },
                }));
              }}
              className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
            />
            <label
              htmlFor="isOneDay"
              className="ml-2 block text-sm text-gray-900"
            >
              This is a one-day workshop
            </label>
          </div>
        </div>

        {/* Fees Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Full Course Fee
            </label>
            <input
              type="number"
              name="fees.fullCourseFee"
              value={formData.fees.fullCourseFee}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Registration Fee
            </label>
            <input
              type="number"
              name="fees.registrationFee"
              value={formData.fees.registrationFee}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Number of Installments
            </label>
            <input
              type="number"
              name="fees.numberOfInstallments"
              value={formData.fees.numberOfInstallments}
              onChange={handleChange}
              min="1"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              One-off Discount (%)
            </label>
            <input
              type="number"
              name="fees.oneoffDiscount"
              value={formData.fees.oneoffDiscount}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              min="0"
              max="100"
            />
          </div>
        </div>

        {/* Minimum Qualifications */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Minimum Qualifications
          </label>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="olRequired"
                name="olRequired"
                checked={formData.minimumQualifications.olRequired}
                onChange={handleCheckboxChange}
                className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
              />
              <label
                htmlFor="olRequired"
                className="ml-2 text-sm text-gray-900"
              >
                GCE O/L Required
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="alRequired"
                name="alRequired"
                checked={formData.minimumQualifications.alRequired}
                onChange={handleCheckboxChange}
                className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
              />
              <label
                htmlFor="alRequired"
                className="ml-2 text-sm text-gray-900"
              >
                GCE A/L Required
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-900">
                Work Experience (months):
              </label>
              <input
                type="number"
                name="workExperience"
                value={formData.minimumQualifications.workExperience}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    minimumQualifications: {
                      ...prev.minimumQualifications,
                      workExperience: parseInt(e.target.value) || 0,
                    },
                  }))
                }
                min="0"
                className="w-24 rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              />
            </div>
          </div>
        </div>

        {/* Batches Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">
              Course Batches
            </label>
            <button
              type="button"
              onClick={addBatch}
              className="text-sm text-pink-700 hover:text-pink-900"
            >
              + Add Batch
            </button>
          </div>

          {Array.isArray(formData.batches) &&
            formData.batches.map((batch, index) => (
              <div key={index} className="border rounded-md p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-sm font-medium">Batch {index + 1}</h4>
                    <p className="text-xs text-gray-500">ID: {batch.batchId}</p>
                  </div>
                  {formData.batches.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeBatch(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600">
                      Start Date
                    </label>
                    <div className="grid grid-cols-[minmax(4.5rem,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2">
                      <input
                        type="number"
                        value={batch.startDate.year}
                        onChange={(e) =>
                          handleBatchChange(
                            index,
                            'startDate.year',
                            e.target.value,
                          )
                        }
                        placeholder="Year"
                        min="1900"
                        max="9999"
                        className="mt-1 block min-w-0 w-full tabular-nums rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                      />
                      <input
                        type="number"
                        value={batch.startDate.month}
                        onChange={(e) =>
                          handleBatchChange(
                            index,
                            'startDate.month',
                            e.target.value,
                          )
                        }
                        placeholder="Month"
                        min="1"
                        max="12"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                      />
                      <input
                        type="number"
                        value={batch.startDate.date}
                        onChange={(e) =>
                          handleBatchChange(
                            index,
                            'startDate.date',
                            e.target.value,
                          )
                        }
                        placeholder="Date"
                        min="1"
                        max="31"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600">
                      Branch
                    </label>
                    <select
                      value={
                        batch.branchId && branchesById[batch.branchId]
                          ? batch.branchId
                          : ''
                      }
                      onChange={(e) => {
                        const selected = branchesById[e.target.value];
                        if (!selected) return;
                        handleBatchChange(index, 'branchId', selected.id);
                        handleBatchChange(index, 'branchName', selected.name);
                        handleBatchChange(
                          index,
                          'branch',
                          selected.slug || selected.name.toLowerCase(),
                        );
                      }}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                    >
                      {activeBranches.length === 0 ? (
                        <option value="">No branches configured</option>
                      ) : null}
                      {!batch.branchId && activeBranches.length ? (
                        <option value="">Select a branch...</option>
                      ) : null}
                      {activeBranches.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                      {batch.branchId && !branchesById[batch.branchId] ? (
                        <option value={batch.branchId}>
                          {batch.branchName || batch.branch || 'Legacy branch'}
                        </option>
                      ) : null}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600">
                      Maximum Students
                    </label>
                    <input
                      type="number"
                      value={batch.maxStudents}
                      onChange={(e) =>
                        handleBatchChange(index, 'maxStudents', e.target.value)
                      }
                      min="1"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      Class Days
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {scheduleDayOptions.map((day) => {
                        const isSelected =
                          batch.schedule?.days?.includes(day) || false;

                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleBatchDay(index, day)}
                            className={`px-3 py-1 rounded-md text-xs font-medium border ${
                              isSelected
                                ? 'border-pink-500 bg-pink-50 text-pink-800'
                                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600">
                      Time Slot
                    </label>
                    <input
                      type="text"
                      value={batch.schedule?.timeSlot || ''}
                      onChange={(e) =>
                        handleBatchChange(
                          index,
                          'schedule.timeSlot',
                          e.target.value,
                        )
                      }
                      placeholder="09:00-12:00"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600">
                      Lessons Per Week
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={batch.schedule?.lessonsPerWeek || 1}
                      onChange={(e) =>
                        handleBatchChange(
                          index,
                          'schedule.lessonsPerWeek',
                          e.target.value,
                        )
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600">
                    Schedule Notes
                  </label>
                  <textarea
                    value={batch.schedule?.notes || ''}
                    onChange={(e) =>
                      handleBatchChange(index, 'schedule.notes', e.target.value)
                    }
                    rows="2"
                    placeholder="Optional notes, holidays, or timetable constraints"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  />
                </div>
              </div>
            ))}
        </div>

        {/* Additional Fields */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Introduction Video URL
            </label>
            <input
              type="url"
              name="introVideoUrl"
              value={formData.introVideoUrl}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Course Image
            </label>
            {displayedImageUrl && (
              <div className="mt-2 flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                <img
                  src={displayedImageUrl}
                  alt={`${formData.name || 'Course'} cover`}
                  className="h-16 w-24 rounded object-cover"
                />
                <span className="text-sm text-gray-600">
                  {courseImage
                    ? 'New cover image preview. Save the course to apply it.'
                    : 'Current cover image will be preserved unless you upload a new one.'}
                </span>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-1 block w-full"
            />
          </div>
        </div>

        {/* Instructors Section - Add this before the form actions */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">
              Assign Instructors
            </label>
            {availableInstructors.length === 0 && (
              <span className="text-sm text-gray-500">
                No instructors available
              </span>
            )}
          </div>

          {availableInstructors.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableInstructors.map((instructor) => (
                <div
                  key={instructor.id}
                  className={`p-4 rounded-lg border ${
                    formData.instructors.includes(instructor.id)
                      ? 'border-pink-500 bg-pink-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id={`instructor-${instructor.id}`}
                      checked={formData.instructors.includes(instructor.id)}
                      onChange={() => handleInstructorChange(instructor.id)}
                      className="mt-1 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                    />
                    <div>
                      <label
                        htmlFor={`instructor-${instructor.id}`}
                        className="block text-sm font-medium text-gray-900"
                      >
                        {instructor.name}
                      </label>
                      <p className="text-sm text-gray-500">
                        {instructor.email}
                      </p>
                      {instructor.specialization && (
                        <p className="text-sm text-gray-500">
                          {instructor.specialization}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {formData.instructors.length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-gray-600">
                Selected Instructors: {formData.instructors.length}
              </p>
            </div>
          )}
        </div>

        {/* Curriculum Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">
              Course Curriculum
            </label>
            <button
              type="button"
              onClick={addCurriculumItem}
              className="text-sm text-pink-700 hover:text-pink-900"
            >
              + Add Topic
            </button>
          </div>

          {formData.curriculum.map((item, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) =>
                      handleCurriculumChange(index, 'title', e.target.value)
                    }
                    placeholder="Topic Title"
                    className="w-full border rounded-md p-2"
                  />
                  <textarea
                    value={item.description}
                    onChange={(e) =>
                      handleCurriculumChange(
                        index,
                        'description',
                        e.target.value,
                      )
                    }
                    placeholder="Topic Description"
                    className="mt-2 w-full border rounded-md p-2"
                    rows="2"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeCurriculumItem(index)}
                  className="ml-2 text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Payment Summary */}
        <div className="bg-gray-50 p-4 rounded-md">
          <h2 className="text-lg font-medium mb-4">Payment Summary</h2>
          <div className="space-y-2">
            <p>
              Installment Amount: {formatCurrency(calculateInstallmentAmount())}
            </p>
            <p>
              One-off Payment (with {formData.fees.oneoffDiscount}% discount):{' '}
              {formatCurrency(calculateOneoffPayment())}
            </p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/courses/available')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            {hasCourseChanges ? 'Cancel' : 'Go back'}
          </button>
          <button
            type="submit"
            disabled={loading || !hasCourseChanges}
            className="rounded-md bg-pink-600 px-4 py-2 text-white hover:bg-pink-700 disabled:opacity-50"
          >
            {loading
              ? 'Saving...'
              : isEditMode
              ? 'Update Course'
              : 'Create Course'}
          </button>
        </div>
      </form>
      </div>
    </WorkspaceShell>
  );
};

export default CourseForm;
